import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Plus, Trash2, UserX } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import {
    getGuestUsers,
    getGuestsInvitations,
    deleteInvitation,
    deactivateUser,
    deleteUser,
} from '../../services/apiService';
import { useToast } from '../../hooks/use-toast';
import FacilitiesTable from './tables/FacilitiesTable';
import AddGuestDialog from './AddGuestDialog';
import LoadingRing from '../ui-components/LoadingRing';

interface Guest {
    _id: string;
    email: string;
    status: 'pending' | 'active' | 'expired';
    activationDate?: string;
    expiryDate?: string;
}

interface GuestAction {
    guest: Guest;
    type: 'revoke' | 'deactivate' | 'delete';
}

const ACTION_CONFIG = {
    revoke: {
        title: 'Revoke invitation?',
        description:
            'This will revoke the guest invitation. You can always invite them again.',
        confirm: 'Revoke',
        loading: 'Revoking...',
        success: 'Invitation revoked',
    },
    deactivate: {
        title: 'Deactivate guest?',
        description:
            'The end date for this guest will be set to today, removing their access.',
        confirm: 'Deactivate',
        loading: 'Deactivating...',
        success: 'Guest deactivated',
    },
    delete: {
        title: 'Delete guest?',
        description: 'This will permanently delete this guest account.',
        confirm: 'Delete',
        loading: 'Deleting...',
        success: 'Guest deleted',
    },
};

const STATUS_STYLES: Record<Guest['status'], string> = {
    pending:
        'border-amber-300/15 bg-amber-300/5 text-amber-200',
    active:
        'border-emerald-300/15 bg-emerald-300/5 text-emerald-200',
    expired:
        'border-white/10 bg-white/5 text-muted-foreground',
};

function StatusBadge({ status }: { status: Guest['status'] }) {
    return (
        <Badge
            variant="outline"
            className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}
        >
            {status}
        </Badge>
    );
}

function DaysRemainingCell({ guest }: { guest: Guest }) {
    if (!guest.expiryDate) return <span className="text-xs text-muted-foreground">—</span>;
    const days = Math.ceil(
        (new Date(guest.expiryDate).getTime() - Date.now()) / (1000 * 3600 * 24)
    );
    return (
        <span className="text-xs tabular-nums text-muted-foreground">
            {days > 0 ? `${days}d left` : days === 0 ? 'Today' : `${Math.abs(days)}d ago`}
        </span>
    );
}

function formatDate(date?: string) {
    if (!date) return '—';
    return new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function getActionForStatus(status: Guest['status']): GuestAction['type'] {
    if (status === 'pending') return 'revoke';
    if (status === 'active') return 'deactivate';
    return 'delete';
}

function getActionIcon(type: GuestAction['type']) {
    if (type === 'delete') return <Trash2 className="mr-1 h-3 w-3" aria-hidden="true" />;
    return <UserX className="mr-1 h-3 w-3" aria-hidden="true" />;
}

function getActionLabel(type: GuestAction['type']) {
    if (type === 'revoke') return 'Revoke';
    if (type === 'deactivate') return 'Deactivate';
    return 'Delete';
}

interface GuestsSectionProps {
    search: string;
}

export default function GuestsSection({ search }: GuestsSectionProps) {
    const { toast } = useToast();
    const [guests, setGuests] = useState<Guest[]>([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<GuestAction | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchGuests = useCallback(async () => {
        setLoading(true);
        try {
            const [invitations, users] = await Promise.all([
                getGuestsInvitations(),
                getGuestUsers(),

            ]);
            setGuests([...invitations, ...users] as Guest[]);
        } catch {
            toast({
                title: 'Could not load guests',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchGuests();
    }, [fetchGuests]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return guests;
        return guests.filter((g) => g.email.toLowerCase().includes(q));
    }, [guests, search]);

    const handleAction = async () => {
        if (!pendingAction) return;
        const { guest, type } = pendingAction;
        setActionLoading(true);
        try {
            if (type === 'revoke') {
                await deleteInvitation(guest._id);
            } else if (type === 'deactivate') {
                await deactivateUser(guest._id, new Date());
            } else {
                await deleteUser(guest._id);
            }
            toast({ title: ACTION_CONFIG[type].success });
            setPendingAction(null);
            await fetchGuests();
        } catch {
            toast({
                title: `Failed to ${type} guest`,
                variant: 'destructive',
            });
        } finally {
            setActionLoading(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'email',
                header: 'Email',
                className: 'w-[30%]',
                cell: (g: Guest) => (
                    <span className="text-sm text-foreground">{g.email}</span>
                ),
            },
            {
                key: 'status',
                header: 'Status',
                className: 'w-[12%]',
                cell: (g: Guest) => <StatusBadge status={g.status} />,
            },
            {
                key: 'startDate',
                header: 'Start date',
                className: 'w-[16%]',
                cell: (g: Guest) => (
                    <span className="text-xs text-foreground">
                        {formatDate(g.activationDate)}
                    </span>
                ),
            },
            {
                key: 'endDate',
                header: 'End date',
                className: 'w-[16%]',
                cell: (g: Guest) => (
                    <span className="text-xs text-foreground">
                        {formatDate(g.expiryDate)}
                    </span>
                ),
            },
            {
                key: 'daysRemaining',
                header: 'Remaining',
                className: 'w-[14%]',
                cell: (g: Guest) => <DaysRemainingCell guest={g} />,
            },
            {
                key: 'actions',
                header: '',
                className: 'w-[12%] text-right',
                headerClassName: 'text-right',
                cell: (g: Guest) => {
                    const type = getActionForStatus(g.status);
                    return (
                        <div className="flex justify-end">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPendingAction({ guest: g, type });
                                }}
                            >
                                {getActionIcon(type)}
                                {getActionLabel(type)}
                            </Button>
                        </div>
                    );
                },
            },
        ],
        []
    );

    return (
        <>
            <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                    {filtered.length} guest{filtered.length !== 1 ? 's' : ''}
                </p>
                <Button size="sm" onClick={() => setAddOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                    Add Guest
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-16 text-sm text-muted-foreground">
                    <LoadingRing size="large" />
                </div>
            ) : (
                <FacilitiesTable
                    facilities={filtered}
                    columns={columns}
                    emptyMessage="No guests found."
                />
            )}

            <AddGuestDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                onAdded={fetchGuests}
            />

            {pendingAction && (
                <AlertDialog
                    open={Boolean(pendingAction)}
                    onOpenChange={(open) => {
                        if (!open) setPendingAction(null);
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {ACTION_CONFIG[pendingAction.type].title}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {ACTION_CONFIG[pendingAction.type].description}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={actionLoading}>
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                disabled={actionLoading}
                                onClick={handleAction}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                {actionLoading
                                    ? ACTION_CONFIG[pendingAction.type].loading
                                    : ACTION_CONFIG[pendingAction.type].confirm}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </>
    );
}
